/**
 * ProductEditPage - Product Edit Page with Tab-based Layout
 *
 * Modern, professional edit page with:
 * - Tab navigation (Tổng quan, Nội dung, Giá/Kho, SEO)
 * - Sticky action bar
 * - Collapsible sections
 * - Responsive design
 */

import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { connect } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import {
	DateBox,
	LoadingPanel,
	NumberBox,
	TextAreaBox,
	TextBox,
} from '../../../components/base';
import { ImageBox } from '../../../components/base/ImageBox';
import { EditPageLayout, EditPageSection, EditPageField } from '../../../components/container';
import { ProductButton } from '../button-actions/ProductButton';
import { ChangeStatusConfirm } from '../change-status-confirm';
import ComboBoxProductCategory from '../../../components/shared/hoc-product-category/ComboBoxProductCategory';
import { eBaseActionStatus } from '../../../context/base/eBaseActionStatus';
import { ProductActionConfirmRequest } from '../../../context/models/products/ProductActionConfirmRequest';
import {
	ProductModel,
	dfProductModel,
	validProductSchema,
} from '../../../context/models/products/ProductModel';
import { eProductStatus, getProductStatusName } from '../../../context/models/products/eProductStatus';
import { ProductActionTypes } from '../../../context/stores/products/IProductActionsTypes';
import { actions } from '../../../context/stores/rootActions';
import { AppState } from '../../../context/stores/rootReducers';
import { slugHelper } from '../../../utils/slugHelper';
import TextEditor from '../../../components/base/TextEditor';
import ProductImageTable from '../product-images';
import { Package, FileText, DollarSign, Search, Image as ImageIcon, Info } from 'lucide-react';
import { NotifyHelper } from '../../../utils/NotifyHelper';

type ProductEditLocationState = {
	returnTo?: string;
	returnState?: unknown;
};

const normalizeHtmlContent = (value: string) => {
	const raw = value || '';
	if (!raw.trim()) return '';

	const textOnly = raw
		.replace(/<[^>]*>/g, '')
		.replace(/&nbsp;/gi, ' ')
		.replace(/\s+/g, '')
		.trim();

	const hasMeaningfulMarkup = /<(img|table|iframe|video|ul|ol|li|blockquote|h[1-6]|hr|pre|code|figure)\b/i.test(raw);

	return !textOnly && !hasMeaningfulMarkup ? '' : raw;
};

const mapStateToProps = (state: AppState) => {
	return {
		status: state.product.status,
		detailResponse: state.product.detailResponse,
		selectedIds: state.product.selectedIds,
		showConfirm: state.product.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<ProductActionTypes>) => ({
	handleGetDetail: (id: number) => {
		dispatch(actions.product.detailRequest(id));
	},
	handleSaveData: (data: ProductModel) => {
		dispatch(actions.product.saveRequest(data));
	},
	handleChangeStatus: (detail: ProductActionConfirmRequest) => {
		dispatch(
			actions.product.changeStatusRequest({
				actionStatus: detail.actionStatus,
				productIds: detail.ids || [],
				receivedBy: detail.receivedBy || '',
				message: detail.message || '',
				subject: detail.subject || '',
			})
		);
	},
	onShowConfirm: (detail: ProductActionConfirmRequest) => {
		dispatch(actions.product.showConfirm(detail));
	},
});

type IProductEditPageProps = {
	status: eBaseActionStatus;
	detailResponse?: ProductModel;
	selectedIds: number[];
	showConfirm: ProductActionConfirmRequest;
	handleGetDetail: (id: number) => void;
	handleSaveData: (data: ProductModel) => void;
	handleChangeStatus: (data: ProductActionConfirmRequest) => void;
	onShowConfirm: (data: ProductActionConfirmRequest) => void;
};

const ProductEditPage = (props: IProductEditPageProps) => {
	const { id } = useParams();
	const productId = useMemo(() => parseInt(id || '0'), [id]);
	const { pathname, state: locationState } = useLocation();
	const navigate = useNavigate();
	const editLocationState = (locationState as ProductEditLocationState | undefined) || {};
	const productListPath = useMemo(() => {
		if (pathname.startsWith('/products-published')) return '/products-published';
		if (pathname.startsWith('/products-deleted')) return '/products-deleted';
		if (pathname.startsWith('/products-removed')) return '/products-removed';
		return '/products-template';
	}, [pathname]);
	const returnToPath = editLocationState.returnTo || productListPath;
	const returnState = editLocationState.returnState;
	const shouldGoBack = Boolean(editLocationState.returnTo);

	const { detailResponse, status, showConfirm, selectedIds, handleGetDetail, handleSaveData, onShowConfirm } =
		props;

	const [initialValues, setInitialValues] = useState<ProductModel>(dfProductModel);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const isInitializedRef = useRef(false);

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		formState: { errors },
	} = useForm<ProductModel>({
		resolver: yupResolver(validProductSchema() as any),
		mode: 'all',
		defaultValues: dfProductModel,
	});

	const syncFormValues = useCallback(
		(values: ProductModel) => {
			(Object.keys(values) as (keyof ProductModel)[]).forEach((field) => {
				setValue(field, values[field] as any, { shouldDirty: false, shouldValidate: false });
			});
		},
		[setValue]
	);

	// Helper to update form values and mark as unsaved
	const updateField = useCallback((field: keyof ProductModel, value: any) => {
		setInitialValues((prev) => {
			const nextValue = value;
			const nextValues = { ...prev, [field]: nextValue };
			setValue(field, nextValue, { shouldDirty: true, shouldValidate: true });
			return nextValues;
		});
		setHasUnsavedChanges(true);
	}, [setValue]);

	const onSubmitHandler = (values: ProductModel) => {
		handleSaveData({
			...values,
			summary: normalizeHtmlContent(values.summary),
			contentHtml: normalizeHtmlContent(values.contentHtml),
		});
		setHasUnsavedChanges(false);
	};

	const handleInvalidSubmit = (formErrors: typeof errors) => {
		const fieldLabels: Partial<Record<keyof ProductModel, string>> = {
			name: 'Tên sản phẩm',
			alias: 'Slug',
			catId: 'Danh mục',
			sku: 'SKU',
			price: 'Giá bán',
			priceOnSale: 'Giá giảm',
			image: 'Ảnh bìa',
			summary: 'Đặc tả sản phẩm',
			contentHtml: 'Nội dung giới thiệu',
		};

		const invalidFields = Object.keys(formErrors) as (keyof ProductModel)[];
		if (invalidFields.length === 0) {
			NotifyHelper.Warning('Vui lòng kiểm tra lại dữ liệu trước khi lưu.');
			return;
		}

		const invalidLabels = invalidFields
			.map((field) => fieldLabels[field] || String(field))
			.filter(Boolean);
		NotifyHelper.Warning(`Không thể lưu vì còn thiếu hoặc sai: ${invalidLabels.join(', ')}.`);
	};

	const watchChangeName = useWatch({ control, name: 'name', defaultValue: '' });
	const watchAlias = useWatch({ control, name: 'alias', defaultValue: '' });

	// Auto-generate slug when name changes
	useEffect(() => {
		if (initialValues.status !== eProductStatus.PUBLISHED) {
			const slug = slugHelper.toSlug(watchChangeName);
			if (watchAlias !== slug) {
				setValue('alias', slug);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [watchChangeName, watchAlias, initialValues.status, setValue]);

	// Load detail data
	useEffect(() => {
		if (detailResponse) {
			const newValues = {
				...detailResponse,
				publishedOnDate: moment(detailResponse.publishedOnDate).format(),
				actionStatus: eProductStatus.EDITED,
			};
			setInitialValues(newValues);
			reset(newValues);
			syncFormValues(newValues);
			setHasUnsavedChanges(false);
			isInitializedRef.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailResponse, reset, syncFormValues]);

	// Initialize form on mount
	useEffect(() => {
		if (productId > 0) {
			handleGetDetail(productId);
		} else {
			const defaultValues = dfProductModel;
			setInitialValues(defaultValues);
			reset(defaultValues);
			syncFormValues(defaultValues);
			isInitializedRef.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productId, reset, syncFormValues]);

	// Handle reload status
	useEffect(() => {
		if (status === eBaseActionStatus.reload) {
			if (shouldGoBack) {
				navigate(-1);
				return;
			}
			navigate(returnToPath, { replace: true, state: returnState });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status, navigate, returnToPath, returnState, shouldGoBack]);

	// Handle cancel
	const handleCancel = () => {
		if (shouldGoBack) {
			navigate(-1);
			return;
		}
		navigate(returnToPath, { replace: true, state: returnState });
	};

	// Format status for display
	const getStatusBadge = () => {
		switch (initialValues.status) {
			case eProductStatus.PUBLISHED:
				return { label: 'Đã đăng bán', variant: 'success' as const };
			case eProductStatus.DRAFT:
				return { label: 'Bản nháp', variant: 'secondary' as const };
			case eProductStatus.DELETED:
				return { label: 'Đã xóa', variant: 'destructive' as const };
			default:
				return { label: 'Chỉnh sửa', variant: 'warning' as const };
		}
	};

	const statusBadge = getStatusBadge();

	const sectionChecklist = useMemo(() => {
		const normalizedSummary = normalizeHtmlContent(initialValues.summary);
		const normalizedContentHtml = normalizeHtmlContent(initialValues.contentHtml);
		const overview = [
			Boolean(initialValues.name?.trim()),
			Boolean(watchAlias?.trim()),
			Boolean(initialValues.catId),
			Boolean(initialValues.image?.trim()),
		];
		const content = [
			Boolean(normalizedSummary),
			Boolean(normalizedContentHtml),
		];
		const pricing = [
			Boolean(initialValues.price && initialValues.price > 0),
			Boolean(initialValues.priceOnSale && initialValues.priceOnSale > 0),
			Boolean(initialValues.publishedOnDate),
		];
		const seo = [
			Boolean(initialValues.metaTitle?.trim()),
			Boolean(initialValues.metaDescription?.trim()),
		];

		const countDone = (items: boolean[]) => items.filter(Boolean).length;

		return {
			overview: { done: countDone(overview), total: overview.length },
			content: { done: countDone(content), total: content.length },
			pricing: { done: countDone(pricing), total: pricing.length },
			seo: { done: countDone(seo), total: seo.length },
		};
	}, [initialValues, watchAlias]);

	// Breadcrumbs for navigation
	const breadcrumbs = [
		{ label: 'Trang chủ', href: '/' },
		{ label: 'Sản phẩm', href: productListPath },
		{ label: productId > 0 ? 'Chỉnh sửa sản phẩm' : 'Tạo mới sản phẩm' },
	];

	// Tab content: Overview Tab
	const overviewTabContent = (
		<div className='space-y-6'>

			{/* Product Name & Cover Image */}
			<EditPageSection
				title='Thông tin cơ bản'
				icon={<Info className='h-4 w-4' />}
			>
				<div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
					<EditPageField
						label='Tên sản phẩm'
						required
						error={errors.name?.message}
						helpText='Tên hiển thị trên trang sản phẩm'
					>
						<TextAreaBox
							name='name'
							value={initialValues.name}
							onValueChanged={(value) => {
								updateField('name', value);
							}}
							className='form-control-simple no-resize editor-focus editor-f-22'
							classWrapper='border-bottom'
							sizeClass='sm'
							placeholder='Nhập tên sản phẩm...'
						/>
					</EditPageField>

					<div className='space-y-4'>
						<EditPageField label='Slug' error={errors.alias?.message}>
							<TextBox
								name='alias'
								value={watchAlias}
								isDisabled={initialValues.status === eProductStatus.PUBLISHED}
								onValueChanged={(value) => {
									updateField('alias', value);
								}}
								sizeClass='sm'
								className='form-control-simple editor-focus editor-f-16'
								classWrapper='border-bottom'
								helpBlock='Link: /san-pham/ten-san-pham'
							/>
						</EditPageField>

						<EditPageField label='Danh mục' error={errors.catId?.message}>
							<ComboBoxProductCategory
								name='catId'
								value={initialValues.catId}
								onValueChanged={(value) => {
									updateField('catId', value);
								}}
							/>
						</EditPageField>
					</div>
				</div>

				<div className='mt-6'>
					<EditPageField label='Ảnh bìa'>
						<ImageBox
							name='image'
							value={initialValues.image}
							isDisabled={initialValues.name.length <= 0}
							file_name={initialValues.name}
							onValueChanged={(value) => {
								updateField('image', value || '');
							}}
						/>
					</EditPageField>
				</div>
			</EditPageSection>

			{/* Short Description */}
			<EditPageSection
				title='Mô tả ngắn'
				icon={<FileText className='h-4 w-4' />}
			>
			<TextAreaBox
				name='initContent'
				value={initialValues.initContent}
				onValueChanged={(value) => {
					updateField('initContent', value);
				}}
				className='form-control-simple no-resize editor-focus editor-f-18'
				classWrapper='border-bottom'
				sizeClass='sm'
				placeholder='Mô tả ngắn gọn về sản phẩm...'
			/>
			</EditPageSection>

			{/* Gallery */}
			<EditPageSection
				title='Thư viện ảnh'
				icon={<ImageIcon className='h-4 w-4' />}
				contentClassName='border-t-0 !px-4 pb-0 pt-1 sm:px-0'
			>
				<ProductImageTable
					productId={productId}
					title={detailResponse?.name || ''}
					description={detailResponse?.initContent || ''}
				/>
			</EditPageSection>
		</div>
	);

	// Tab content: Content Tab
	const contentTabContent = (
		<div className='space-y-6'>
			{/* Specification */}
			<EditPageSection
				title='Đặc tả sản phẩm'
				icon={<FileText className='h-4 w-4' />}
			>
				<TextEditor
					name='summary'
					value={initialValues.summary}
					onValueChanged={(value) => {
						updateField('summary', value);
					}}
				/>
			</EditPageSection>

			{/* Full Content */}
			<EditPageSection
				title='Nội dung giới thiệu'
				icon={<FileText className='h-4 w-4' />}
			>
				<TextEditor
					name='contentHtml'
					value={initialValues.contentHtml}
					onValueChanged={(value) => {
						updateField('contentHtml', value);
					}}
				/>
			</EditPageSection>
		</div>
	);

	// Tab content: Pricing Tab
	const pricingTabContent = (
		<div className='space-y-6'>
			{/* Basic Pricing */}
			<EditPageSection
				title='Thông tin giá'
				icon={<DollarSign className='h-4 w-4' />}
			>
				<div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4'>
					<EditPageField label='SKU' helpText='Mã sản phẩm'>
						<TextBox
							name='sku'
							value={initialValues.sku}
							onValueChanged={(value) => {
								updateField('sku', value);
							}}
							sizeClass='sm'
						/>
					</EditPageField>

					<EditPageField
						label='Giá niêm yết'
						error={errors.price?.message}
						helpText='Giá gốc (VNĐ)'
					>
						<NumberBox
							name='price'
							value={initialValues.price}
							onValueChanged={(value) => {
								updateField('price', value);
							}}
							sizeClass='sm'
						/>
					</EditPageField>

					<EditPageField
						label='Giá bán'
						error={errors.priceOnSale?.message}
						helpText='Giá sau giảm (VNĐ)'
					>
						<NumberBox
							name='priceOnSale'
							value={initialValues.priceOnSale}
							onValueChanged={(value) => {
								updateField('priceOnSale', value);
							}}
							sizeClass='sm'
						/>
					</EditPageField>

					<EditPageField label='Ngày đăng bán'>
						<DateBox
							name='publishedOnDate'
							value={moment(initialValues.publishedOnDate).format()}
							displayFormat={'dd/MM/yyyy HH:mm'}
							typeDate='datetime'
							onValueChanged={(e) => {
								updateField('publishedOnDate', e.value);
							}}
						/>
					</EditPageField>
				</div>
			</EditPageSection>

			{/* Inventory placeholder */}
			<EditPageSection
				title='Quản lý kho'
				icon={<Package className='h-4 w-4' />}
			>
				<div className='text-sm text-muted-foreground'>
					<p>Quản lý số lượng tồn kho và trạng thái sản phẩm.</p>
					<div className='grid grid-cols-1 gap-4 mt-4 md:grid-cols-3'>
						<div className='p-4 bg-muted/50 rounded-lg'>
							<p className='text-muted-foreground'>Số lượng tồn</p>
							<p className='text-2xl font-bold'>--</p>
						</div>
						<div className='p-4 bg-muted/50 rounded-lg'>
							<p className='text-muted-foreground'>Đã bán</p>
							<p className='text-2xl font-bold'>--</p>
						</div>
						<div className='p-4 bg-muted/50 rounded-lg'>
							<p className='text-muted-foreground'>Lượt xem</p>
							<p className='text-2xl font-bold'>--</p>
						</div>
					</div>
				</div>
			</EditPageSection>
		</div>
	);

	// Tab content: SEO Tab
	const seoTabContent = (
		<div className='space-y-6'>
			<EditPageSection
				title='Công cụ tìm kiếm'
				icon={<Search className='h-4 w-4' />}
				defaultExpanded={false}
			>
				<div className='space-y-6'>
					{/* SEO Preview */}
					<div className='p-4 bg-muted/30 rounded-lg'>
						<p className='text-sm font-medium mb-3'>Xem trước kết quả tìm kiếm</p>
						<div className='bg-background p-4 rounded-lg border'>
							<p className='text-blue-600 text-lg hover:underline'>
								{initialValues.metaTitle || initialValues.name || 'Tiêu đề sản phẩm'}
							</p>
							<p className='text-green-700 text-sm'>
									https://example.com/san-pham/{watchAlias || 'ten-san-pham'}
							</p>
							<p className='text-muted-foreground text-sm mt-1'>
								{initialValues.metaDescription || initialValues.initContent || 'Mô tả sản phẩm sẽ hiển thị ở đây...'}
							</p>
						</div>
					</div>

					<EditPageField
						label='Meta Title'
						helpText='Tiêu đề hiển thị trên Google (tối đa 60 ký tự)'
					>
						<TextBox
							name='metaTitle'
							value={initialValues.metaTitle}
							onValueChanged={(value) => {
								updateField('metaTitle', value);
							}}
							sizeClass='sm'
							className='form-control-simple editor-focus editor-f-18'
							classWrapper='border-bottom'
						/>
					</EditPageField>

					<EditPageField
						label='Meta Description'
						helpText='Mô tả ngắn cho SEO (tối đa 160 ký tự)'
					>
						<TextAreaBox
							name='metaDescription'
							value={initialValues.metaDescription}
							onValueChanged={(value) => {
								updateField('metaDescription', value);
							}}
							sizeClass='sm'
							className='form-control-simple editor-focus editor-f-18'
							classWrapper='border-bottom'
						/>
					</EditPageField>
				</div>
			</EditPageSection>
		</div>
	);

	// Tabs configuration
	const tabs = [
		{ id: 'overview', label: 'Tổng quan', icon: <Package className='h-4 w-4' />, badge: `${sectionChecklist.overview.done}/${sectionChecklist.overview.total}`, content: overviewTabContent },
		{ id: 'content', label: 'Nội dung', icon: <FileText className='h-4 w-4' />, badge: `${sectionChecklist.content.done}/${sectionChecklist.content.total}`, content: contentTabContent },
		{ id: 'pricing', label: 'Giá & Kho', icon: <DollarSign className='h-4 w-4' />, badge: `${sectionChecklist.pricing.done}/${sectionChecklist.pricing.total}`, content: pricingTabContent },
		{ id: 'seo', label: 'SEO', icon: <Search className='h-4 w-4' />, badge: `${sectionChecklist.seo.done}/${sectionChecklist.seo.total}`, content: seoTabContent },
	];

	// Action buttons
	const actionButtons = (
		<ProductButton
			isOwner={true}
			productStatus={initialValues.status}
			isActionDisable={true}
			onActionConfirm={(actionChange) => {
				onShowConfirm({ isShow: true, actionStatus: actionChange, ids: [] });
				setInitialValues((prev) => ({ ...prev, actionStatus: actionChange }));
			}}
				handleSubmit={handleSubmit(onSubmitHandler as any, handleInvalidSubmit as any)}
			isSaving={status === eBaseActionStatus.saving}
		/>
	);

	if (status === eBaseActionStatus.loading && !detailResponse) {
		return (
			<div className='min-h-screen flex items-center justify-center'>
				<LoadingPanel />
			</div>
		);
	}

	return (
		<>
			<EditPageLayout
				title='Thông tin sản phẩm'
				description={`${productId > 0 ? 'Chỉnh sửa' : 'Tạo mới'} sản phẩm`}
				tabs={tabs}
				actions={actionButtons}
				isSaving={status === eBaseActionStatus.saving}
				onSave={handleSubmit(onSubmitHandler as any, handleInvalidSubmit as any)}
				onCancel={handleCancel}
				breadcrumbs={breadcrumbs}
				status={statusBadge.label}
				statusVariant={statusBadge.variant}
				lastModified={detailResponse?.lockedOnDate || detailResponse?.publishedOnDate}
				hasUnsavedChanges={hasUnsavedChanges}
			/>

			{showConfirm && showConfirm.isShow && (
				<ChangeStatusConfirm
					isShow={showConfirm.isShow}
					onClose={() => onShowConfirm({ isShow: false, actionStatus: showConfirm.actionStatus, ids: [] })}
					onConfirmStatus={handleSubmit(onSubmitHandler as any, handleInvalidSubmit as any)}
					isLoading={status === eBaseActionStatus.loading}
					mesageText={`Bạn chắc chắn muốn ${getProductStatusName(showConfirm.actionStatus)} sản phẩm này?`}
					actionStatus={showConfirm.actionStatus}
					selectedIds={selectedIds}
				/>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductEditPage);
